import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface Student {
  id: number;
  name: string;
  score: number;
  avatar: string;
  badge: 'gold' | 'silver' | 'bronze' | 'none';
  improvement: number;
  location: string;
}

interface FilterTab {
  id: string;
  label: string;
  icon: string;
}

const WiziRankSection: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('overall');

  const filterTabs: FilterTab[] = [
    { id: 'overall', label: 'Overall', icon: 'fas fa-trophy' },
    { id: 'physics', label: 'Physics', icon: 'fas fa-atom' },
    { id: 'chemistry', label: 'Chemistry', icon: 'fas fa-flask' },
    { id: 'mathematics', label: 'Mathematics', icon: 'fas fa-calculator' },
    { id: 'biology', label: 'Biology', icon: 'fas fa-dna' }
  ];

  const topStudents: Student[] = [
    {
      id: 1,
      name: 'Arjun Sharma',
      score: 98.5,
      avatar: 'AS',
      badge: 'gold',
      improvement: 15,
      location: 'Delhi'
    },
    {
      id: 2,
      name: 'Priya Patel',
      score: 96.2,
      avatar: 'PP',
      badge: 'silver',
      improvement: 12,
      location: 'Mumbai'
    },
    {
      id: 3,
      name: 'Rohit Kumar',
      score: 94.8,
      avatar: 'RK',
      badge: 'bronze',
      improvement: 8,
      location: 'Bangalore'
    },
    {
      id: 4,
      name: 'Sneha Reddy',
      score: 92.1,
      avatar: 'SR',
      badge: 'none',
      improvement: 20,
      location: 'Hyderabad'
    },
    {
      id: 5,
      name: 'Vikash Singh',
      score: 90.7,
      avatar: 'VS',
      badge: 'none',
      improvement: 6,
      location: 'Pune'
    },
    {
      id: 6,
      name: 'Ananya Gupta',
      score: 89.3,
      avatar: 'AG',
      badge: 'none',
      improvement: 18,
      location: 'Chennai'
    },
    {
      id: 7,
      name: 'Kiran Joshi',
      score: 87.9,
      avatar: 'KJ',
      badge: 'none',
      improvement: 10,
      location: 'Kolkata'
    }
  ];

  const getBadgeColor = (badge: string) => {
    switch (badge) {
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'bronze': return '#CD7F32';
      default: return '#6c757d';
    }
  };

  const getBadgeIcon = (badge: string) => {
    switch (badge) {
      case 'gold': return 'fas fa-crown';
      case 'silver': return 'fas fa-medal';
      case 'bronze': return 'fas fa-award';
      default: return 'fas fa-star';
    }
  };

  return (
    <div className="container-fluid py-5 bg-light">
      <div className="container py-5">
        <div className="text-center pb-2 wow fadeInUp" data-wow-delay="0.1s">
          <h4 className="text-primary mb-3">WiziRank Leaderboard</h4>
          <h1 className="display-4 mb-4">Climb the <span className="wiziai-gradient-text">WiziRank Ladder</span></h1>
          <p className="mb-1 fs-5">
            Compete with fellow students, track your progress, and celebrate achievements on our dynamic leaderboard
          </p>
        </div>

        <div className="row g-5 align-items-start">
          {/* Leaderboard Section */}
          <div className="col-lg-8 wow fadeInLeft" data-wow-delay="0.2s">
            {/* Filter Tabs */}
            <div className="bg-white rounded-4 shadow-sm p-3 mb-4">
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                {filterTabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`btn btn-sm rounded-pill px-3 py-2 ${
                      activeFilter === tab.id 
                        ? 'btn-primary' 
                        : 'btn-outline-primary'
                    }`}
                    onClick={() => setActiveFilter(tab.id)}
                  >
                    <i className={`${tab.icon} me-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Leaderboard */}
            <div className="bg-white rounded-4 shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-primary text-white p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-trophy me-2"></i>
                    Top Performers - {filterTabs.find(t => t.id === activeFilter)?.label}
                  </h5>
                  <div className="bg-white bg-opacity-20 rounded-pill px-3 py-1">
                    <small className="text-danger">
                      <i className="fas fa-clock me-1"></i>
                      Updated 2 min ago
                    </small>
                  </div>
                </div>
              </div>

              {/* Top 3 Podium */}
              <div className="p-4 bg-light">
                <div className="row g-3 justify-content-center">
                  {/* Second Place */}
                  <div className="col-md-4 order-md-1 order-2">
                    <div className="text-center">
                      <div className="position-relative d-inline-block mb-3">
                        <div className="bg-silver rounded-circle d-flex align-items-center justify-content-center text-white fw-bold" 
                             style={{ width: '80px', height: '80px', backgroundColor: getBadgeColor('silver') }}>
                          {topStudents[1].avatar}
                        </div>
                        <div className="position-absolute top-0 end-0">
                          <i className={getBadgeIcon('silver')} style={{ color: getBadgeColor('silver'), fontSize: '1.5rem' }}></i>
                        </div>
                      </div>
                      <h6 className="mb-1">{topStudents[1].name}</h6>
                      <div className="text-white fw-bold fs-4">{topStudents[1].score}%</div>
                      <small className="text-white">{topStudents[1].location}</small>
                    </div>
                  </div>

                  {/* First Place */}
                  <div className="col-md-4 order-md-2 order-1">
                    <div className="text-center position-relative">
                      <div className="position-absolute top-0 start-50 translate-middle">
                        <i className="fas fa-crown text-warning fa-2x"></i>
                      </div>
                      <div className="position-relative d-inline-block mb-3 mt-4">
                        <div className="bg-gold rounded-circle d-flex align-items-center justify-content-center text-white fw-bold border border-warning border-3" 
                             style={{ width: '100px', height: '100px', backgroundColor: getBadgeColor('gold') }}>
                          {topStudents[0].avatar}
                        </div>
                        <div className="position-absolute bottom-0 end-0">
                          <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center" 
                               style={{ width: '30px', height: '30px' }}>
                            <span className="text-white fw-bold">1</span>
                          </div>
                        </div>
                      </div>
                      <h5 className="mb-1 text-primary">{topStudents[0].name}</h5>
                      <div className="text-warning fw-bold fs-3">{topStudents[0].score}%</div>
                      <small className="text-muted">{topStudents[0].location}</small>
                      <div className="mt-2">
                        <span className="badge bg-success">
                          <i className="fas fa-arrow-up me-1"></i>
                          +{topStudents[0].improvement}% this week
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Third Place */}
                  <div className="col-md-4 order-md-3 order-3">
                    <div className="text-center">
                      <div className="position-relative d-inline-block mb-3">
                        <div className="bg-bronze rounded-circle d-flex align-items-center justify-content-center text-white fw-bold" 
                             style={{ width: '80px', height: '80px', backgroundColor: getBadgeColor('bronze') }}>
                          {topStudents[2].avatar}
                        </div>
                        <div className="position-absolute top-0 end-0">
                          <i className={getBadgeIcon('bronze')} style={{ color: getBadgeColor('bronze'), fontSize: '1.5rem' }}></i>
                        </div>
                      </div>
                      <h6 className="mb-1">{topStudents[2].name}</h6>
                      <div className="text-white fw-bold fs-4">{topStudents[2].score}%</div>
                      <small className="text-white">{topStudents[2].location}</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remaining Rankings */}
              <div className="p-0">
                {topStudents.slice(3).map((student, index) => (
                  <div key={student.id} className="d-flex align-items-center p-4 border-bottom">
                    <div className="me-3">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center fw-bold text-white" 
                           style={{ width: '40px', height: '40px' }}>
                        {index + 4}
                      </div>
                    </div>
                    <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center text-white fw-bold me-3" 
                         style={{ width: '50px', height: '50px' }}>
                      {student.avatar}
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{student.name}</h6>
                      <small className="text-muted">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {student.location}
                      </small>
                    </div>
                    <div className="text-center me-3">
                      <div className="text-primary fw-bold fs-5">{student.score}%</div>
                      {student.improvement > 0 && (
                        <small className="text-success">
                          <i className="fas fa-arrow-up me-1"></i>
                          +{student.improvement}%
                        </small>
                      )}
                    </div>
                    <div>
                      <i className={`${getBadgeIcon('none')} text-muted`}></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Stats */}
          <div className="col-lg-4 wow fadeInRight" data-wow-delay="0.4s">
            <div className="bg-white rounded-4 shadow-lg p-4 mb-4">
              <h5 className="mb-4">
                <i className="fas fa-chart-bar text-primary me-2"></i>
                Your Rank Status
              </h5>
              <div className="text-center mb-4">
                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" 
                     style={{ width: '80px', height: '80px' }}>
                  <span className="text-white fw-bold fs-4">YU</span>
                </div>
                <h6>Your Current Rank</h6>
                <div className="text-success fw-bold fs-2">#23</div>
                <small className="text-success">Out of 2,847 students</small>
              </div>
              
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <div className="bg-success bg-opacity-10 rounded-3 p-3 text-center">
                    <div className="text-white fw-bold">+8</div>
                    <small className="text-white">Positions Up</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="bg-primary bg-opacity-10 rounded-3 p-3 text-center">
                    <div className="text-white fw-bold">87.3%</div>
                    <small className="text-white">Your Score</small>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <small className="text-muted">Progress to Top 10</small>
                  <small className="text-primary fw-bold">3.2% to go</small>
                </div>
                <div className="bg-light rounded-pill" style={{ height: '8px' }}>
                  <div className="bg-primary rounded-pill" style={{ height: '8px', width: '65%' }}></div>
                </div>
              </div>

              <Link to="/leaderboard" className="btn btn-primary w-100">
                <i className="fas fa-list me-2"></i>
                View Full Leaderboard
              </Link>
            </div>

            {/* Achievement Badges */}
            <div className="bg-white rounded-4 shadow-lg p-4">
              <h5 className="mb-4">
                <i className="fas fa-trophy text-warning me-2"></i>
                Recent Achievements
              </h5>
              <div className="space-y-3">
                <div className="d-flex align-items-center p-3 bg-warning bg-opacity-10 rounded-3">
                  <div className="text-warning me-3">
                    <i className="fas fa-fire fa-2x"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Hot Streak!</h6>
                    <small className="text-white">5 tests in a row above 85%</small>
                  </div>
                </div>
                <div className="d-flex align-items-center p-3 bg-success bg-opacity-10 rounded-3">
                  <div className="text-success me-3">
                    <i className="fas fa-rocket fa-2x"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Rising Star</h6>
                    <small className="text-white">Jumped 15 ranks this month</small>
                  </div>
                </div>
                <div className="d-flex align-items-center p-3 bg-primary bg-opacity-10 rounded-3">
                  <div className="text-primary me-3">
                    <i className="fas fa-brain fa-2x"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Physics Master</h6>
                    <small className="text-white">Top 5 in Physics category</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-5 wow fadeInUp" data-wow-delay="0.6s">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="bg-primary text-white rounded-3 p-4 p-md-5">
                <h3 className="text-white mb-3">Ready to Climb Higher?</h3>
                <p className="text-white mb-4 fs-5">
                  Take more tests, improve your scores, and see your name rise up the WiziRank leaderboard!
                </p>
                <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                  <Link to="/tests" className="btn btn-light btn-lg px-4 py-3">
                    <i className="fas fa-play me-2"></i>Take a Test Now
                  </Link>
                  <Link to="/practice" className="btn btn-outline-light btn-lg px-4 py-3">
                    <i className="fas fa-dumbbell me-2"></i>Practice More
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WiziRankSection;
